import { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Plane, Trash2, MapPin } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { openUpgradeModalFromError } from "@/components/UpgradeModal";

const OCCASIONS = ["Office", "Casual", "Party", "Wedding", "Beach", "Hiking", "Business Meeting", "Date", "Festival"];

const DESTINATION_HERO = "https://images.unsplash.com/photo-1488646953014-85cb44e25828";

export default function Travel() {
  const [plans, setPlans] = useState([]);
  const [open, setOpen] = useState(false);
  const [destination, setDestination] = useState("");
  const [days, setDays] = useState(5);
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [picked, setPicked] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null);
  const [itinerary, setItinerary] = useState("");
  const [parsing, setParsing] = useState(false);

  const parseItinerary = async () => {
    setParsing(true);
    try {
      const { data } = await api.post("/travel/parse-itinerary", { text: itinerary });
      const p = data.parsed || {};
      if (p.destination) setDestination(p.destination);
      if (p.days) setDays(p.days);
      if (p.start_date) setStartDate(p.start_date);
      if (p.occasions) setPicked(p.occasions);
      if (data.missing?.length) {
        toast.info(data.message || "Please fill in remaining fields.");
      } else {
        toast.success("Itinerary parsed — review and generate.");
      }
    } catch (e) {
      const d = e.response?.data?.detail;
      if (typeof d === "object" && d?.error === "missing_info") {
        toast.error(d.message || "Add more itinerary detail.");
      } else {
        toast.error("Could not parse itinerary. Fill the fields manually.");
      }
    } finally { setParsing(false); }
  };

  const load = async () => {
    const { data } = await api.get("/travel/plans");
    setPlans(data);
    if (data.length && !active) setActive(data[0]);
  };
  useEffect(() => { load(); }, []);

  const togglePicked = (o) => setPicked((p) => p.includes(o) ? p.filter((x) => x !== o) : [...p, o]);

  const generate = async () => {
    if (!destination.trim()) { toast.error("Destination required"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/travel/plans", {
        destination, days: Number(days), start_date: startDate, occasions: picked,
      });
      setPlans((p) => [data, ...p]);
      setActive(data);
      setOpen(false);
      setDestination(""); setPicked([]);
      toast.success(`Trip to ${data.destination} planned`);
    } catch (e) {
      // 402 → user needs Travel Pass or Premium → open upgrade modal.
      if (e?.response?.status === 402 && openUpgradeModalFromError(e, "travel")) {
        return;
      }
      toast.error(e?.response?.data?.detail?.message || "Could not generate plan");
    } finally { setLoading(false); }
  };

  const remove = async (id) => {
    await api.delete(`/travel/plans/${id}`);
    setPlans((p) => p.filter((x) => x.id !== id));
    if (active?.id === id) setActive(null);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-16">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="eyebrow flex items-center gap-2"><Plane size={12} /> Travel Planner</p>
          <h1 className="editorial-h1 mt-3">Pack like an editor.</h1>
        </div>
        <Button
          data-testid="travel-new-btn"
          onClick={() => setOpen(!open)}
          className="rounded-none uppercase tracking-[0.2em] text-xs h-11"
        >
          {open ? "Cancel" : "New trip"}
        </Button>
      </div>

      {open && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-8 border border-border p-6 lg:p-8 bg-card"
        >
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label className="eyebrow">Destination</Label>
              <Input
                data-testid="travel-destination-input"
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
                placeholder="Paris, Bali, Tokyo…"
                className="rounded-none mt-1.5 h-11"
              />
            </div>
            <div>
              <Label className="eyebrow">Days</Label>
              <Input data-testid="travel-days-input" type="number" min="1" max="30" value={days} onChange={(e) => setDays(e.target.value)} className="rounded-none mt-1.5 h-11" />
            </div>
            <div>
              <Label className="eyebrow">Start date</Label>
              <Input data-testid="travel-date-input" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="rounded-none mt-1.5 h-11" />
            </div>
          </div>
          <div className="mt-6">
            <Label className="eyebrow">Occasions during the trip</Label>
            <div className="mt-3 flex flex-wrap gap-2">
              {OCCASIONS.map((o) => {
                const on = picked.includes(o);
                return (
                  <button
                    key={o}
                    onClick={() => togglePicked(o)}
                    data-testid={`travel-occ-${o}`}
                    className={`px-3 py-2 border text-[11px] uppercase tracking-[0.18em] ${
                      on ? "bg-accent text-accent-foreground border-foreground" : "border-border hover:border-foreground"
                    }`}
                  >{o}</button>
                );
              })}
            </div>
          </div>
          <Button
            data-testid="travel-generate-btn"
            onClick={generate}
            disabled={loading}
            className="mt-6 rounded-none uppercase tracking-[0.2em] text-xs h-11"
          >
            {loading ? <><Loader2 size={14} className="animate-spin mr-2" /> Planning…</> : "Generate plan"}
          </Button>

          <div className="mt-8 border-t border-border pt-6">
            <p className="eyebrow">Or paste an itinerary</p>
            <textarea
              data-testid="travel-itinerary-input"
              value={itinerary}
              onChange={(e) => setItinerary(e.target.value)}
              placeholder="Day 1 — Land in Paris, dinner in Marais. Day 2 — Louvre, then jazz club. Day 3 — Montmartre & flight home."
              className="mt-3 w-full min-h-[100px] p-3 border border-border bg-background rounded-none text-sm"
            />
            <Button
              data-testid="travel-parse-btn"
              variant="outline"
              onClick={parseItinerary}
              disabled={parsing || !itinerary.trim()}
              className="mt-3 rounded-none uppercase tracking-[0.18em] text-[10px] h-10"
            >
              {parsing ? <><Loader2 size={12} className="animate-spin mr-2" />Parsing</> : "Auto-fill from itinerary"}
            </Button>
          </div>
        </motion.div>
      )}

      {plans.length === 0 && !open && (
        <div className="mt-10 relative overflow-hidden border border-border">
          <img src={DESTINATION_HERO} alt="" className="h-72 w-full object-cover" />
          <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center text-white text-center">
            <p className="font-serif text-4xl">Plan your first trip.</p>
            <p className="text-sm mt-2 opacity-80">Stylebay builds a capsule wardrobe + daily outfits from your closet.</p>
          </div>
        </div>
      )}

      {plans.length > 0 && (
        <div className="mt-10 grid lg:grid-cols-12 gap-6">
          <aside className="lg:col-span-3 space-y-2">
            {plans.map((p) => (
              <button
                key={p.id}
                data-testid={`travel-plan-${p.id}`}
                onClick={() => setActive(p)}
                className={`w-full text-left border p-4 transition ${
                  active?.id === p.id ? "border-foreground" : "border-border hover:border-foreground"
                }`}
              >
                <p className="font-serif text-xl tracking-tight">{p.destination}</p>
                <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">{p.days} days · {p.start_date}</p>
              </button>
            ))}
          </aside>

          {active && (
            <div className="lg:col-span-9 space-y-6">
              <div className="border border-border p-6 flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <p className="eyebrow flex items-center gap-2"><MapPin size={12} /> {active.start_date} · {active.days} days</p>
                  <p className="font-serif text-4xl mt-2 tracking-tight">{active.destination}</p>
                  <p className="text-sm text-muted-foreground mt-2 max-w-xl">{active.weather_summary}</p>
                </div>
                <Button variant="outline" onClick={() => remove(active.id)} className="rounded-none text-xs uppercase tracking-[0.2em]">
                  <Trash2 size={12} className="mr-1.5" /> Delete
                </Button>
              </div>

              <div className="border border-border p-6">
                <p className="eyebrow">Packing checklist</p>
                <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-2">
                  {(active.packing_checklist || []).map((it, i) => (
                    <div key={i} className={`border p-3 ${it.item_id_or_null ? "border-border" : "border-dashed border-border bg-muted"}`}>
                      <p className="text-sm">{it.type}</p>
                      <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-1">{it.color}</p>
                      <p className="text-xs text-muted-foreground mt-2">{it.reason}</p>
                      {!it.item_id_or_null && <p className="text-[10px] uppercase tracking-[0.2em] mt-2 text-destructive">Buy</p>}
                    </div>
                  ))}
                </div>
              </div>

              <div className="border border-border p-6">
                <p className="eyebrow">Day-by-day looks</p>
                <div className="mt-4 space-y-4">
                  {(active.schedule || []).map((d, i) => (
                    <div key={i} className="border border-border p-4">
                      <div className="flex items-baseline justify-between flex-wrap gap-2">
                        <p className="font-serif text-2xl tracking-tight">Day {d.day} <span className="text-base font-sans text-muted-foreground">· {d.date}</span></p>
                        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{d.occasion} · {d.weather}</p>
                      </div>
                      <div className="mt-4 grid grid-cols-4 gap-3">
                        <Tile label="Top" item={d.outfit?.top} />
                        <Tile label="Bottom" item={d.outfit?.bottom} />
                        <Tile label="Shoes" item={d.outfit?.shoes} />
                        <Tile label="Accessory" item={d.outfit?.accessories?.[0]} />
                      </div>
                      {d.outfit?.notes && <p className="mt-3 text-sm text-muted-foreground italic">{d.outfit.notes}</p>}
                    </div>
                  ))}
                </div>
              </div>

              {active.style_tips?.length > 0 && (
                <div className="border border-border p-6 bg-accent text-accent-foreground">
                  <p className="text-[10px] uppercase tracking-[0.25em] opacity-80">Stylist's tips</p>
                  <ul className="mt-3 space-y-1.5 text-sm">
                    {active.style_tips.map((t, i) => <li key={i}>· {t}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Tile({ label, item }) {
  return (
    <div className="border border-border bg-card">
      <div className="aspect-square bg-muted overflow-hidden">
        {item ? (
          <img src={fileUrl(item.image_path)} alt={item.type} className="h-full w-full object-cover" />
        ) : (
          <div className="h-full flex items-center justify-center text-[10px] uppercase tracking-[0.2em] text-muted-foreground">—</div>
        )}
      </div>
      <p className="p-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</p>
      {item && <p className="px-2 pb-2 text-xs truncate">{item.type}</p>}
    </div>
  );
}
